/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RaiseEETicket.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { componentStyle } from "../../../config/appTheme";
import RaiseTicketTemplate from "./RaiseTicketTemplate";
import SelectTemplate from "./SelectDialog/SelectTemplate";
import { useDispatch, useSelector } from "react-redux";
import DialogIndex from "./SelectDialog/DialogIndex";
import KeyValueDisplayCard from "./SelectDialog/KeyValueDisplayCard";
import CustomCheckBoxWithoutFormControl from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxWithoutFormControl";
import RaiseTicketRightTemplate from "./RaiseTicketRightTemplate";
import { CAPABILITY_CODE } from "../../../config/appConstants";
import EESWMSavedLinkageCard from "./EESWMSavedLinkageCard";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import { HandleApiActions } from "../../../redux/actions/actionHandler";
import CustomToolTip from "../../../components/infoButton/CustomToolTip";
import { ENTITY } from "../../../components/customComponentsV2/SmartMatchingDialogContainer/constants/entityTypes";
import AISuggestionDialogContainer from "../../../components/customComponentsV2/SmartMatchingDialogContainer/AiSuggestionDialogContainer";
import { legalRequirements, userManagementApis } from "../../../redux/actions";
import testCasesActions from "../../../redux/actions/testCasesActions";
import TestCaseCampaignLinkDialog from "./TestCaseCampaignLinkDialog";

function RaiseEETicket({
  savedLinkages,
  setSavedLinkages,
  setDisableRaiseTicketButton,
  entityType,
  onBeforeSmartMatch,
}) {
  const dispatch = useDispatch();
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const [zoneList, setZoneList] = useState([]);
  const [isZonalProject, setIsZonalProject] = useState(false);

  const [selectedEEMain, setSelectedEEMain] = useState(null);
  const [openEESelect, setOpenEESelect] = useState(false); const [formResetKey, setFormResetKey] = useState(0);


  const [selectedLegalMain, setSelectedLegalMain] = useState(null);
  const [disabledLegalRequirement, setDisabledLegalRequirement] = useState([]);
  const [openLegalSelect, setOpenLegalSelect] = useState(false);
  const [disabledEEComponents, setDisabledEEComponents] = useState([]);

  const [openTestCaseSelect, setOpenTestCaseSelect] = useState(false);
  const [selectedTestCaseForDisplay, setSelectedTestCaseForDisplay] =
    useState(null);
  const [selectedTextCaseMain, setSelectedTestCaseMain] = useState(null);
  const [selectedComponentRequestor, setSelectedComponentRequestor] = useState(
    [],
  );
  const [selectedLegalTechUserDetails, setSelectedLegalTechUserDetails] =
    useState([]);

  const [selectedComplianceRequestors, setSelectedComplianceRequestor] =
    useState([]);
  const [selectedComplianceReviewers, setSelectedComplianceReviewers] =
    useState([]);
  const [selectedComplianceSupplier, setSelectedComplianceSupplier] = useState(
    [],
  );
  const [description, setDescription] = useState("");
  const [
    selectedComponentSupplierDetails,
    setSelectedComponentSupplierDetails,
  ] = useState([]);
  const [selectedDynamicForm, setSelectedDynamicForm] = useState(null);

  /* Smart-matching states */
  const [openAISuggestion, setOpenAISuggestion] = useState(false);
  const [showSmartMatchDialog, setshowSmartMatchDialog] = useState(false);
  const [componentToBeMatched, setComponentToBeMatched] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [
    selectedSmartMatchTestCaseSuggestions,
    setSelectedSmartMatchTestCaseSuggestions,
  ] = useState([]);
  // Prefill context coming from Smart Match
  const [prefillContext, setPrefillContext] = useState(null);

  const [allUsersList, setAllUserList] = useState([]);

  const [disableSmartMatch, setDisableSmartMatch] = useState(false);
  const [closeAiDialogOnAdd, setCloseAiDialogOnAdd] = useState(false);
  const [openCampaignLinkDialog, setOpenCampaignLinkDialog] = useState(false);
  const [testCasesToCampaignLink, setTestCasesToCampaignLink] = useState([]);

  const commitSelectedTestCases = (testCases = []) => {
    if (!Array.isArray(testCases) || testCases.length === 0) {
      return;
    }

    setSelectedTestCaseMain((prev) => {
      const currentSelection = Array.isArray(prev) ? prev : [];
      const mergedSelection = [...currentSelection];

      testCases.forEach((testCase) => {
        const alreadySelected = mergedSelection.some(
          (selectedItem) =>
            selectedItem?.test_case_id === testCase?.test_case_id,
        );
        if (!alreadySelected) {
          mergedSelection.push({ ...testCase, is_campaign_linked: true });
        }
      });

      setSelectedTestCaseForDisplay(mergedSelection);
      return mergedSelection;
    });
  };

  const clearCampaignLinkFields = () => {
    setOpenCampaignLinkDialog(false);
    setTestCasesToCampaignLink([]);
  };

  const openCampaignDialogForTestCases = (testCases = []) => {
    setTestCasesToCampaignLink(testCases);
    setOpenCampaignLinkDialog(true);
  };

  const handleSelectedTestCases = (testCases = []) => {
    const currentSelectedIds = new Set(
      (selectedTextCaseMain || []).map((tc) => tc?.test_case_id),
    );
    const testCasesReadyForTicket = testCases.filter(
      (tc) =>
        tc?.is_campaign_linked !== false ||
        currentSelectedIds.has(tc?.test_case_id),
    );
    const unlinkedTestCases = testCases.filter(
      (tc) =>
        tc?.is_campaign_linked === false &&
        !currentSelectedIds.has(tc?.test_case_id),
    );

    setSelectedTestCaseMain(testCasesReadyForTicket);
    setSelectedTestCaseForDisplay(testCasesReadyForTicket);

    if (unlinkedTestCases.length > 0) {
      openCampaignDialogForTestCases(unlinkedTestCases);
    }
  };

  const handleSmartMatchRequest = async (componentDetails, tag) => {
    setDisableSmartMatch(true);
    setCloseAiDialogOnAdd(tag === ENTITY.TYPE.EE || tag === ENTITY.TYPE.LEGAL);

    const componentName =
      tag === ENTITY.TYPE.EE
        ? componentDetails?.description
        : selectedEEMain?.description;
    if (componentName?.length > 0) {
      setComponentToBeMatched([componentName]);
    } else {
      setComponentToBeMatched(["smart-match"]);
    }

    const sourceType =
      tag === ENTITY.TYPE.LEGAL
        ? ENTITY.TYPE.EE
        : tag === ENTITY.TYPE.EE
          ? ENTITY.TYPE.LEGAL
          : ENTITY.TYPE.LEGAL;

    const sourceId =
      tag === ENTITY.TYPE.LEGAL
        ? componentDetails?.ee_id
        : componentDetails?.lr_id;

    const shouldUseEEScope = tag === ENTITY.TYPE.TEST_CASE;

    setshowSmartMatchDialog(true);
    setOpenAISuggestion(true);
    setSelectedEntity({
      source: [{ entityType: sourceType, entityId: sourceId }],
      targetEntityTypes: [tag],
      topK: 20,
      minConfidence: 0.1,
      description: componentName,
      scope: shouldUseEEScope
        ? {
          projectId: Number(selectedProjectDetails?.project_id),
          variantId: Number(selectedVariantDetails?.variant_id),
          scopeEntities: [
            {
              entityType: ENTITY.TYPE.EE,
              entityId: Number(selectedEEMain?.ee_id),
            },
          ],
        }
        : {
          projectId: Number(selectedProjectDetails?.project_id),
          variantId: Number(selectedVariantDetails?.variant_id),
        },
    });
    setDisableSmartMatch(false);
  };

  const handleLinkSuggestions = (recommendation) => {
    if (recommendation.type === "EE") {
      setSelectedEEMain(recommendation);
      getEELinkedLRList({ eeId: recommendation?.ee_id });
      setOpenAISuggestion(false);
    } else if (recommendation.type === "LEGAL_REQUIREMENT") {
      handleLegalAddClick(recommendation);
      setOpenAISuggestion(false);
    } else if (recommendation.type === "TEST_CASE") {
      handleTestCaseAddClick(recommendation);
    }
  };

  const handleLegalAddClick = (recommendation) => {
    fetchLegalRecommendationContext({ lr_id: recommendation.lr_id });
  };

  const fetchLegalRecommendationContext = async (params) => {
    const resp = await dispatch(
      HandleApiActions({
        https_method: "GET",
        ...legalRequirements.get_legal_requirements_details_new,
        params: params,
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );
    if (!resp.error && resp.data && resp.data.length > 0) {
      const legalRequirementData = resp.data[0];
      setSelectedLegalMain(legalRequirementData);
      setSelectedLegalTechUserDetails(legalRequirementData.tech_service_users);
    }
  };

  const handleTestCaseAddClick = (recommendation) => {
    fetchTestCaseRecommendationContext({
      manual_test_case_id: recommendation.manual_test_case_id,
    });
  };

  const fetchTestCaseRecommendationContext = async (params) => {
    const resp = await dispatch(
      HandleApiActions({
        https_method: "GET",
        ...testCasesActions.get_test_cases,
        params: params,
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );
    if (!resp.error && resp.data && resp.data.length > 0) {
      const testCaseData = resp.data[0];
      if (testCaseData?.is_campaign_linked === false) {
        openCampaignDialogForTestCases([testCaseData]);
      } else {
        setSelectedSmartMatchTestCaseSuggestions((prev) => {
          if (
            prev.some((tc) => tc.test_case_id === testCaseData.test_case_id)
          ) {
            return prev;
          }
          return [...prev, testCaseData];
        });
      }
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      let resp = await dispatch(
        HandleApiActions({
          ...userManagementApis.get_users_list_pa,
          show_toast: false,
          params: { page_number: 1, page_size: 500 },
        }),
      );
      setAllUserList(resp.data);
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedSmartMatchTestCaseSuggestions.length > 0) {
      setSelectedTestCaseMain(selectedSmartMatchTestCaseSuggestions);
      setSelectedTestCaseForDisplay(selectedSmartMatchTestCaseSuggestions);
    }
  }, [selectedSmartMatchTestCaseSuggestions]);

  const getEELinkedLRList = async ({ eeId }) => {
    if (eeId) {
      let apiDetails = softwareIntersectionAPI.get_ee_legal_requirements;
      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            project_id: Number(selectedProjectDetails.project_id),
            variant_id: Number(selectedVariantDetails?.variant_id),
            ee_id: Number(eeId),
          },
          show_toast: false,
        }),
      );

      if (resp.error) {
        setDisabledLegalRequirement([]);
      } else {
        let linkedLrIds = [];
        if (resp.data[0]?.linked_lr_details?.[0]?.lr_id) {
          linkedLrIds = resp?.data?.[0]?.linked_lr_details?.map((lr) =>
            Number(lr?.lr_id),
          );
        }

        setDisabledLegalRequirement(linkedLrIds);
      }
    }
  };

  const getDisabledEEListForLR = async ({ lrId }) => {
    if (!lrId) {
      setDisabledEEComponents([]);
      return;
    }

    if (
      !selectedProjectDetails?.project_id ||
      !selectedVariantDetails?.variant_id
    ) {
      setDisabledEEComponents([]);
      return;
    }

    const resp = await dispatch(
      HandleApiActions({
        ...softwareIntersectionAPI.get_ee_for_legal_requirements,
        params: {
          project_id: Number(selectedProjectDetails?.project_id),
          variant_id: Number(selectedVariantDetails?.variant_id),
          lr_id: Number(lrId),
        },
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );

    if (resp.error) {
      setDisabledEEComponents([]);
      return;
    }

    const eeIds = resp?.data?.[0]?.ee_ids || [];
    const normalizedIds = eeIds
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
    setDisabledEEComponents(normalizedIds);
  };

  const [dynamicFormFilteredData, setDynamicFormFilteredData] = useState(null);
  const [showDynamicSelect, setShowDynamicSelect] = useState(false);
  useEffect(() => {
    setZoneList(selectedProjectDetails?.project_zones);
    setIsZonalProject(selectedProjectDetails?.is_zonal);
  }, [selectedProjectDetails]);

  useEffect(() => {
    clearForm();
    setSavedLinkages([]);
  }, [selectedProjectDetails, selectedVariantDetails]);

  const clearForm = () => {
    setFormResetKey((k) => k + 1);
    setSelectedEEMain(null);
    setSelectedLegalMain(null);
    setSelectedDynamicForm(null);
    setSelectedComplianceRequestor([]);
    setSelectedComplianceReviewers([]);
    setDescription("");
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedComponentSupplierDetails([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
    setSelectedComplianceSupplier([]);
    setDisabledEEComponents([]);
    clearCampaignLinkFields();
  };

  //This code will refresh the test case
  const resetTestCaseState = () => {
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
  };

  useEffect(() => {
    resetTestCaseState();
  }, [selectedEEMain, selectedLegalMain]);

  useEffect(() => {
    setSelectedTestCaseForDisplay([]);
    setSelectedTestCaseMain([]);
    setSelectedSmartMatchTestCaseSuggestions([]);
  }, [selectedEEMain, selectedLegalMain]);

  useEffect(() => {
    if (selectedLegalMain?.lr_id) {
      getDisabledEEListForLR({ lrId: selectedLegalMain.lr_id });
    } else {
      setDisabledEEComponents([]);
    }
  }, [selectedLegalMain, selectedProjectDetails, selectedVariantDetails]);

  return (
    <Box>
      <Grid container>
        <Grid item sm={12} sx={{ borderBottom: "1px solid black" }}>
          <Box sx={{ display: "flex" }}>
            <Typography sx={{ ...componentStyle.font.cardHeadder }}>
              Select Entities and Fill in the Details
            </Typography>
          </Box>
        </Grid>

        <Grid item sm={12} sx={{ pt: 1 }}>
          <RaiseTicketTemplate
            leftContent={
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                }}
              >
                <Box>
                  <SelectTemplate
                    heading={"Select EE Component"}
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenEESelect(true);
                    }}
                    enableEdit={selectedEEMain !== null}
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={selectedLegalMain !== null}
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(selectedLegalMain, ENTITY.TYPE.EE)
                    }
                    content={
                      selectedEEMain ? (
                        <Box sx={{ pl: 2 }}>
                          <KeyValueDisplayCard
                            data={[
                              {
                                key: "Number",
                                value: selectedEEMain.assembly_number,
                                style: { fontWeight: 600 },
                              },
                              {
                                key: "Description",
                                value: selectedEEMain.description,
                              },
                              {
                                key: "Version",
                                value: selectedEEMain.version,
                              },
                            ]}
                          />{" "}
                        </Box>
                      ) : (
                        <></>
                      )
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Legal Requirement"}
                    isRequired={true}
                    onClickAdd={() => {
                      setOpenLegalSelect(true);
                    }}
                    enableEdit={selectedLegalMain !== null}
                    disableSmartMatch={disableSmartMatch}
                    showSmartMatch={selectedEEMain !== null}
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(selectedEEMain, ENTITY.TYPE.LEGAL)
                    }
                    content={
                      <Box sx={{ pl: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            justifyContent: "left",
                            pl: 1,
                            width: "100%",
                          }}
                        >
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              fontWeight: 600,
                            }}
                          >
                            Legal Standard:
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Name: ${selectedLegalMain
                              ? selectedLegalMain.legal_standard_details?.name
                              : ""
                              }`}
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Number: ${selectedLegalMain
                              ? selectedLegalMain.legal_standard_details
                                ?.number
                              : ""
                              }`}
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Revision: ${selectedLegalMain
                              ? selectedLegalMain.legal_standard_details
                                ?.revision
                              : ""
                              }`}
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              fontWeight: 600,
                            }}
                          >
                            Legal Requirement:
                          </Typography>
                          <Typography
                            sx={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Number: ${selectedLegalMain
                              ? selectedLegalMain.req_number
                              : ""
                              }`}
                          </Typography>

                          <CustomToolTip
                            length={200}
                            style={{
                              ...componentStyle.font.text,
                              textAlign: "left",
                              width: "100%",
                            }}
                          >
                            {`Description: ${selectedLegalMain
                              ? selectedLegalMain.description
                              : ""
                              }`}
                          </CustomToolTip>
                        </Box>
                      </Box>
                    }
                  />
                </Box>

                <Box>
                  <SelectTemplate
                    heading={"Select Test Case"}
                    onClickAdd={() => {
                      setOpenTestCaseSelect(true);
                    }}
                    disableSmartMatch={disableSmartMatch}
                    addButtonDisabled={
                      selectedLegalMain === null || selectedEEMain === null
                    }
                    enableEdit={
                      selectedTestCaseForDisplay !== null &&
                      selectedTestCaseForDisplay.length > 0
                    }
                    showSmartMatch={
                      selectedEEMain !== null && selectedLegalMain !== null
                    }
                    onSmartMatchClick={() =>
                      handleSmartMatchRequest(
                        selectedLegalMain,
                        ENTITY.TYPE.TEST_CASE,
                      )
                    }
                    content={
                      <Box sx={{ pl: 2 }}>
                        {selectedTestCaseForDisplay &&
                          selectedTestCaseForDisplay.length &&
                          selectedTestCaseForDisplay.map((item, index) => (
                            <Box
                              key={index}
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                                position: "relative",
                              }}
                            >
                              <Box
                                sx={{
                                  width: "10%",
                                }}
                              >
                                <CustomCheckBoxWithoutFormControl
                                  value={
                                    selectedTextCaseMain?.some(
                                      (selectedItem) =>
                                        selectedItem?.test_case_id ===
                                        item?.test_case_id,
                                    ) || false
                                  }
                                  onChange={(e) => {
                                    const currentSelection =
                                      selectedTextCaseMain || [];
                                    if (
                                      e.target.checked &&
                                      item?.is_campaign_linked === false
                                    ) {
                                      openCampaignDialogForTestCases([item]);
                                      return;
                                    }
                                    const newSelectedTestCase = e.target.checked
                                      ? currentSelection.some(
                                        (selectedItem) =>
                                          selectedItem?.test_case_id ===
                                          item?.test_case_id,
                                      )
                                        ? currentSelection
                                        : [...currentSelection, item]
                                      : currentSelection.filter(
                                        (selectedItem) =>
                                          selectedItem?.test_case_id !==
                                          item?.test_case_id,
                                      );
                                    setSelectedTestCaseMain(
                                      newSelectedTestCase,
                                    );
                                    setSelectedTestCaseForDisplay(
                                      newSelectedTestCase,
                                    );
                                  }}
                                />
                              </Box>
                              <Box
                                sx={{
                                  width: "60%",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    p: 1,
                                  }}
                                >
                                  <Typography
                                    key={index}
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case ID: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.manual_test_case_id}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    key={index}
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Test Case Title: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.name}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    key={index}
                                    sx={{
                                      ...componentStyle.font.text,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                      display: "flex",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                        fontWeight: 600,
                                      }}
                                    >{`Description: `}</Box>
                                    <Box
                                      sx={{
                                        ...componentStyle.font.text,
                                      }}
                                    >
                                      {item.description}
                                    </Box>
                                  </Typography>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  width: "30%",
                                }}
                              >
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    }
                  />
                </Box>
              </Box>
            }
            rightContent={
              <RaiseTicketRightTemplate
                selectedComponentRequestor={selectedComponentRequestor}
                selectedLegalTechUserDetails={selectedLegalTechUserDetails}
                selectedComplianceRequestors={selectedComplianceRequestors}
                setSelectedComplianceRequestor={setSelectedComplianceRequestor}
                selectedComplianceReviewers={selectedComplianceReviewers}
                setSelectedComplianceReviewers={setSelectedComplianceReviewers}
                description={description}
                setDescription={setDescription}
                selectedComponentSupplierDetails={
                  selectedComponentSupplierDetails
                }
                setSelectedComponentSupplierDetails={
                  setSelectedComponentSupplierDetails
                }
                selectedEEMain={selectedEEMain}
                selectedComponent={selectedEEMain}
                selectedComplianceSupplier={selectedComplianceSupplier}
                setSelectedComplianceSupplier={setSelectedComplianceSupplier}
                formType={entityType}
                selectedDynamicForm={selectedDynamicForm}
                setSelectedDynamicForm={setSelectedDynamicForm}
                dynamicFormFilteredData={dynamicFormFilteredData}
                setDynamicFormFilteredData={setDynamicFormFilteredData}
                showDynamicSelect={showDynamicSelect}
                setShowDynamicSelect={setShowDynamicSelect}
                localSubmitDisabled={
                  selectedEEMain === null ||
                  selectedLegalMain === null ||
                  description === "" ||
                  selectedComplianceRequestors.length === 0 ||
                  description.trim().length < 3 ||
                  selectedComplianceReviewers.length === 0
                }
                handleLocalSaveForm={() => {
                  const existingEE = savedLinkages.find(
                    (item) => item.ee_id === selectedEEMain?.ee_id,
                  );

                  if (existingEE) {
                    const duplicateLR = existingEE.legal.find(
                      (legal) => legal.lr_id === selectedLegalMain?.lr_id,
                    );
                    if (duplicateLR) {
                      alert("Unable to create duplicate ticket");
                      return;
                    }
                    existingEE.legal.push({
                      lr_id: selectedLegalMain?.lr_id,
                      description: description,
                      oem_users: selectedComplianceRequestors.map(
                        (user) => user.value,
                      ),
                      tech_service_users: selectedComplianceReviewers.map(
                        (user) => user.value,
                      ),
                      suppliers: selectedComplianceSupplier.map(
                        (user) => user.value,
                      ),

                      test_case_ids:
                        selectedTextCaseMain?.map((tc) => tc.test_case_id) ||
                        [],
                      test_cases: selectedTextCaseMain || [],
                      ...(showDynamicSelect
                        ? { dynamic_form: dynamicFormFilteredData }
                        : {}),
                    });
                    setSavedLinkages([...savedLinkages]);
                  } else {
                    setSavedLinkages([
                      ...savedLinkages,
                      {
                        ee_id: selectedEEMain?.ee_id,
                        addOn: selectedEEMain,
                        legal: [
                          {
                            lr_id: selectedLegalMain?.lr_id,
                            description: description,
                            oem_users: selectedComplianceRequestors.map(
                              (user) => user.value,
                            ),
                            tech_service_users: selectedComplianceReviewers.map(
                              (user) => user.value,
                            ),
                            suppliers: selectedComplianceSupplier.map(
                              (user) => user.value,
                            ),
                            test_case_ids:
                              selectedTextCaseMain?.map(
                                (tc) => tc.test_case_id,
                              ) || [],
                            test_cases: selectedTextCaseMain || [],
                            ...(showDynamicSelect
                              ? { dynamic_form: dynamicFormFilteredData }
                              : {}),
                          },
                        ],
                      },
                    ]);
                  }
                  clearForm();
                }}
                savedLinkageCard={
                  <EESWMSavedLinkageCard
                    savedLinkages={savedLinkages}
                    setSavedLinkages={setSavedLinkages}
                    onRemove={clearForm}
                  />
                }
              />
            }
          />
        </Grid>
      </Grid>

      <DialogIndex
        key={formResetKey}
        openEESelect={openEESelect}
        setOpenEESelect={setOpenEESelect}
        isZonalProject={isZonalProject}
        zoneList={zoneList}
        savedLinkages={savedLinkages}
        disableLrRadioButton={(item) => {
          const eeExists = savedLinkages.find(
            (linkage) => linkage.ee_id === selectedEEMain?.ee_id,
          );
          return eeExists
            ? eeExists.legal.some((legal) => legal.lr_id === item.lr_id)
            : false;
        }}
        selectedEEMain={selectedEEMain}
        onClickAdd={(data) => {
          setSelectedEEMain(data);

          getEELinkedLRList({ eeId: data.ee_id });
          setOpenEESelect(false);

          const removeDuplicates = (arr = []) => {
            if (!Array.isArray(arr)) return [];
            const seen = new Set();
            return arr.filter((user) => {
              if (!user?.user_id || seen.has(user.user_id)) {
                return false;
              }
              seen.add(user.user_id);
              return true;
            });
          };

          const insertCapabilityCodeToUserList = (userDetails) => {
            if (!userDetails || !Array.isArray(userDetails)) return [];
            let capabilityCodeAddedUser = userDetails.map((u) => {
              for (let au of allUsersList) {
                if (au.user_id == u.user_id) {
                  return {
                    ...u,
                    capability_code: au.role_details.capability_code,
                  };
                }
              }
            });
            return capabilityCodeAddedUser;
          };
          const ownerDetails = data?.owner_details
            ? insertCapabilityCodeToUserList(data?.owner_details)
            : [];
          const coOwnerDetails = data?.co_owner_details
            ? insertCapabilityCodeToUserList(data?.co_owner_details)
            : [];
          const editorDetails = data?.edit_user_details
            ? insertCapabilityCodeToUserList(data?.edit_user_details)
            : [];
          const viewersDetails = data?.view_user_details
            ? insertCapabilityCodeToUserList(data?.view_user_details)
            : [];

          let duplicateRemovedComponentRequestor = removeDuplicates([
            ...ownerDetails,
            ...coOwnerDetails,
          ]);

          setSelectedComponentRequestor(
            duplicateRemovedComponentRequestor.filter((ele) => {
              return ele.capability_code !== CAPABILITY_CODE.supplier;
            }),
          );
          let duplicateRemovedComponentSupplierDetails = removeDuplicates([
            ...coOwnerDetails,
            ...editorDetails,
            ...viewersDetails,
          ]);

          setSelectedComponentSupplierDetails(
            duplicateRemovedComponentSupplierDetails.filter((ele) => {
              return ele.capability_code === CAPABILITY_CODE.supplier;
            }),
          );
        }}
        openLegalSelect={openLegalSelect}
        setOpenLegalSelect={setOpenLegalSelect}
        selectedLegalMain={selectedLegalMain}
        disabledLegalRequirement={disabledLegalRequirement}
        setSelectedLegalMain={setSelectedLegalMain}
        onClickAddLegal={(data) => {
          setSelectedLegalMain(data);
          setOpenLegalSelect(false);
          setSelectedLegalTechUserDetails(data.tech_service_users);
        }}
        openTestCaseSelect={openTestCaseSelect}
        setOpenTestCaseSelect={setOpenTestCaseSelect}
        selectedTextCaseMain={selectedTextCaseMain}
        setSelectedTestCaseMain={setSelectedTestCaseMain}
        onClickAddTestCase={(data) => {
          handleSelectedTestCases(data);
          setOpenTestCaseSelect(false);
        }}
        entityID={selectedEEMain?.ee_id}
        entityType={"ee"}
        prefillContext={prefillContext}
        aiRecommendedEntities={[]}
        disabledEEComponents={disabledEEComponents}
      />
      {showSmartMatchDialog && componentToBeMatched?.length > 0 && (
        <AISuggestionDialogContainer
          open={openAISuggestion}
          onClose={() => setOpenAISuggestion(false)}
          entity={selectedEntity}
          onLinkRequest={handleLinkSuggestions}
          onUnlinkRequest={() => { }}
          closeDialogOnAdd={closeAiDialogOnAdd}
        />
      )}
      <TestCaseCampaignLinkDialog
        open={openCampaignLinkDialog}
        testCasesToLink={testCasesToCampaignLink}
        projectId={selectedProjectDetails?.project_id}
        variantId={selectedVariantDetails?.variant_id}
        onClose={clearCampaignLinkFields}
        onSuccess={commitSelectedTestCases}
      />
    </Box>
  );
}

export default RaiseEETicket;
